import React, { FC, useMemo, useState, useEffect, useRef } from 'react';
import ReactImageFallback from 'react-image-fallback';
import { useDispatch, useSelector, batch } from 'react-redux';
import { TrimItemProps } from '.';
import { Benefits } from '@components/Benefits';
import { Losange } from '@components/Losange';
import Link from 'next/link';
import Icon, { Icons } from '../Icon/index';
import PriceBox from '../PriceBox/Configurable';
import SOLPriceBox from '../PriceBox/SOL';
import { BRAND, isBrandOV, isMarketGB, MARKET, MARKETS } from '../../constants/main';
import routes from '../../constants/routes';
import { useCustomRouter } from '@hooks/useCustomRouter';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { usePrices } from '@hooks/usePriceCalculator';
import { useTranslations } from '@hooks/useTranslations';
import { useGTM } from '@hooks/useGTM';
import UIDuck from '../../redux/commonDucks/ui.duck';
import GlobalDuck from '../../redux/global/global.duck';
import { getCalculateSummary } from '../../services/calculateSummary/calculateSummary.service';
import { ICalculateSummaryRequestData } from '../../services/calculateSummary/calculateSummary.types';
import { CarJourneyType } from '../../services';
import { createImageAltResolver, extractViewFromImageUrl, useExteriorPreviewImage } from '@utils/Image.utils';
import { buildParameterizedPathname, extractParamsFromPathname } from '@utils/url.utils';
import { useGetModelKeywords } from '../OfferListItem/utils/getOfferKeywords';
import { BrandTypes } from '../../types/brands';
import FinanceWidgetDuck from '../../redux/financeWidget/financeWidget.duck';
import { FEATURES_LIST } from '../../context/featureSwitchApp';
import ButtonLink from '@components/ButtonLink';
import { createExternalIdFromDefaultConfiguration, findDefaultConfiguration } from '@utils/Offers.utils';
import { useSummaryDataPromise } from '../OfferListItem/hooks/useSummaryDataPromise';
import { UNAVAILABLE_CAR_IMAGE } from '../../constants/images';
import { getMonthlyPriceObjByType, getFinanceDetailText, getFinanceDetailView } from '@utils/Price.utils';
import { StockFormatedPriceType } from '../../services/stock/models/stockItem.price.model';
import MarketingSticker from '../MarketingSticker';
import { findPromoByPageAndType, EType, EContextPage } from '@utils/Promo.utils';
import { IOffer, IPromo } from '../../interfaces/IOfferItem';
import { PaymentJourneyTypes } from '../../partExchange/interfaces/Default';
import { MainTrimContaine } from '@components/TrimItem/TrimItemStyled';
import { GtmData } from '../../types/gtm';
import { IGlobalState } from 'src/redux/redux.interface';
import {useGlobalDuck} from "@hooks/useGlobalDuck";

export const TrimItemTemplate: FC<TrimItemProps> = ({
    className,
    trim,
    usingFilters,
    newRef,
    trimConfiguration,
    index,
    disabled,
    isMarketingStickerExists,
    childRef,
}) => {
    const router = useCustomRouter();
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const { carJourney } = useGlobalDuck();
    const {
        id,
        _id,
        offers,
        title,
        leadTime,
        specPackSlug,
        specPack,
        engineGearboxFuelSlug,
        exteriorColour,
        exteriorColourSlug,
        interiorColourSlug,
        extraFields,
        engine,
        gearbox,
        fuel,
        importInputs,
    } = trim;
    const resolveImageAlt = createImageAltResolver(BRAND, t);

    const { cash, finance, catalog } = usePrices(trim, true, null, false);

    const {
        priceBoxRef,
        priceBoxContainerRef,
        priceBoxCatalogRef,
        headerRef,
        equipmentSubtitleContainerRef,
        descriptionWrapperRef,
        imageRef,
        financeLabelContainerRef,
        benefitsRef,
        equipmentLisRef,
    } = childRef;

    let carId: string = id || _id;

    offers.sort((a, b) => (a.priority < b.priority && 1) || -1);

    const amountRounding = getMonthlyPriceObjByType(trim.prices, StockFormatedPriceType.EMPLOYEE)?.amountRounding;
    const currentPathParams = extractParamsFromPathname(router.asPath);
    const newPathParams = {
        nameplateBodyStyleSlug: currentPathParams.nameplateBodyStyleSlug,
        speckPackSlug: specPackSlug,
        engineGearboxFuelSlug: engineGearboxFuelSlug,
        exteriorColourSlug: exteriorColourSlug,
        interiorColourSlug: interiorColourSlug,
    };

    const { totalPrice: baseTotalPrice, totalBasicPriceWithoutVat } = cash;
    const {
        totalPrice: baseMonthlyPrice,
        financeProperties: { deposit, apr, hasMonthlyPrices },
    } = finance;
    const { totalPrice: baseCatalogPrice } = catalog;

    const { pushProductClick, pushToDataLayer } = useGTM();
    const isBenefitsEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_BENEFITS_MODE_ENABLED);
    const isLeadTimeEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_LEAD_TIME_ENABLED);

    const defaultConfiguration = findDefaultConfiguration(offers as IOffer[], PaymentJourneyTypes.CASH);
    const allowDefaultConfiguration = isBenefitsEnabled && !usingFilters && defaultConfiguration;
    const imageSource = allowDefaultConfiguration ? defaultConfiguration : trim;

    const financeDetailView = getFinanceDetailView(trim.prices, StockFormatedPriceType.EMPLOYEE);
    const financeDetailText = getFinanceDetailText(trim.prices, StockFormatedPriceType.EMPLOYEE, null, null, null);

    const image = useExteriorPreviewImage(imageSource.images ?? trim.images, 'selector', true);

    if (defaultConfiguration && defaultConfiguration.exteriorColourSlug) {
        newPathParams.exteriorColourSlug = defaultConfiguration.exteriorColourSlug;

        if (defaultConfiguration.interiorColourSlug) {
            newPathParams.interiorColourSlug = defaultConfiguration.interiorColourSlug;
            carId = createExternalIdFromDefaultConfiguration(defaultConfiguration);
        }
    }

    const modelURL = buildParameterizedPathname(routes.CAR, carJourney, newPathParams);

    const handleCustomizeClick = () =>
        batch(() => {
            // Reset finance data after before select vehicle
            dispatch(FinanceWidgetDuck.setFinanceWidgetOutput(null));
            dispatch(FinanceWidgetDuck.setFinancePricesList(null));
            dispatch(FinanceWidgetDuck.setFinanceQuote(null));

            pushProductClick('trim', trim);
            pushToDataLayer({
                eventCategory: 'SolRetail::VehicleFinition',
                eventAction: 'Select::VehicleFinition',
                eventLabel: specPack.title,
            } as GtmData);
        });

    const [state, setState] = useState({
        financialData: {},
        loadingData: false,
    });
    const [processDataError, setProcessDataError] = useState<Error>(null);
    const params: ICalculateSummaryRequestData = {
        carConfigId: carId,
    };

    const openModal = () =>
        dispatch(
            UIDuck.openModal({
                data: {
                    financialData: state.financialData,
                    error: processDataError,
                },
                modalType: UIDuck.MODAL_TYPES.FINANCIAL_SUMMARY_CONFIGURATION,
            })
        );

    if (Object.keys(state.financialData).length !== 0 && state.loadingData) {
        openModal();
        setState({ financialData: state.financialData, loadingData: false });
    }

    const processCalculateSummaryData = () => {
        if (!state.loadingData) {
            setState({ financialData: {}, loadingData: true });
            Promise.all([getCalculateSummary(params)])
                .then(([calculateSummaryData]) => {
                    setState({ financialData: calculateSummaryData, loadingData: true });
                })
                .catch((error) => setProcessDataError(error));
        }
    };

    const isSOLEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SOL_ENABLED);

    const formattedTitle = specPack.title
        .replace('(', '\n (')
        .split('\n')
        .map((str: string) => <div key={str}>{str}</div>);

    const colorsCount = useMemo(() => trimConfiguration?.exteriorColours?.length, [trimConfiguration]);
    const enginesCount = useMemo(() => trimConfiguration?.engineGearboxSlugs?.length, [trimConfiguration]);

    const { processCalculateSummaryDataPromise } = useSummaryDataPromise(params);

    const isPromoEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_PROMO_ENABLED);
    const isMarketingStickerEnabled =
        isPromoEnabled && findPromoByPageAndType(offers as (IOffer & { type: EType })[], EContextPage.trim);
    const modelKeywords = useGetModelKeywords(
        trim,
        allowDefaultConfiguration && defaultConfiguration,
        baseTotalPrice,
        baseMonthlyPrice,
        isBrandOV && specPack.title
    );
    return (
        <MainTrimContaine>
            {disabled && (
                <div className="main-trim-container-disabled">
                    <span>{t('trimItem.disabled')}</span>
                </div>
            )}
            <div
                className={`${className} trim-card ${disabled ? 'disabled' : 'enabled'} ${
                    (isPromoEnabled && isMarketingStickerExists) || isMarketingStickerEnabled ? 'with-sticker' : ''
                }`}
                data-testid={disabled ? 'TESTING_DISABLED_TRIM' : 'TESTING_ENABLED_TRIM'}
            >
                {((isPromoEnabled && isMarketingStickerExists) || isMarketingStickerEnabled) && (
                    <MarketingSticker
                        data={isMarketingStickerEnabled as unknown as Array<IPromo>}
                        page={EContextPage.trim}
                    />
                )}
                {isBenefitsEnabled && <Losange label={offers?.[0]?.losangeLabel} />}
                <div className="trimContainer">
                    <div className="generalInfoContainer">
                        <div ref={headerRef} className="header headerRef">
                            <h2 className="title title--detail" data-testid={`TESTING_TRIM_TITLE_${carId}`}>
                                {formattedTitle}
                            </h2>
                        </div>
                        <div
                            className="equipmentSubtitle equipmentSubtitleContainerRef"
                            data-testid={`TESTING_EQUIPMENT_SUBTITLE_${carId}`}
                            ref={equipmentSubtitleContainerRef}
                        >
                            <p>
                                {engine.title} | {gearbox?.specs.find(({ key }) => key === 'type')?.label.toUpperCase()}{' '}
                                | {fuel.toUpperCase()}
                            </p>
                        </div>
                        <div className="priceBox">
                            {isMarketGB ? (
                                <SOLPriceBox
                                    basePrice={baseTotalPrice}
                                    monthlyPrice={baseMonthlyPrice}
                                    catalogPrice={baseCatalogPrice}
                                    calculateSummaryData={processCalculateSummaryDataPromise}
                                    isHomePage
                                    itemId={id || _id}
                                    apr={apr}
                                    childRef={{
                                        priceBoxRef,
                                        priceBoxCatalogRef,
                                        financeLabelContainerRef,
                                        priceBoxContainerRef,
                                    }}
                                    hasMonthlyPrices={hasMonthlyPrices}
                                    amountRounding={amountRounding}
                                    financeDetailView={financeDetailView}
                                    financeDetailText={financeDetailText}
                                />
                            ) : (
                                <PriceBox
                                    basePrice={baseTotalPrice}
                                    monthlyPrice={baseMonthlyPrice}
                                    catalogPrice={baseCatalogPrice}
                                    totalBasicPriceWithoutVat={totalBasicPriceWithoutVat}
                                    depositPrice={deposit}
                                    calculateSummaryData={processCalculateSummaryDataPromise}
                                    isHomePage
                                    promotionalText={extraFields?.promotionalText}
                                    childRef={{
                                        priceBoxRef,
                                        priceBoxCatalogRef,
                                        financeLabelContainerRef,
                                        priceBoxContainerRef,
                                    }}
                                    itemId={id || _id}
                                    amountRounding={amountRounding}
                                    financeDetailView={financeDetailView}
                                    financeDetailText={financeDetailText}
                                    apr={apr}
                                    hasMonthlyPrices={hasMonthlyPrices}
                                    importInputs={importInputs}
                                    index={index}
                                    tabIndex={disabled ? -1 : 0}
                                />
                            )}
                            {Boolean(isLeadTimeEnabled) && (
                                <div className="priceInfo" data-testid={`TESTING_LEADTIME_${carId}`}>
                                    {BRAND !== BrandTypes.DS && (
                                        <span className="priceInfo__clockIcon">
                                            <Icon
                                                name={Icons.Clock}
                                                height={BRAND === BrandTypes.AP ? 16 : 12}
                                                width={BRAND === BrandTypes.AP ? 16 : 12}
                                            />
                                        </span>
                                    )}

                                    {BRAND === BrandTypes.DS && (
                                        <span className="priceInfo__clockIcon">
                                            <Icon name={Icons.ClockV2} height={12} width={12} />
                                        </span>
                                    )}

                                    <span className="priceInfo__text">
                                        {` ${t('orderPanel.delivery.available')}
                        ${leadTime} ${t('orderPanel.delivery.available.months')}`}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div
                        ref={imageRef}
                        className={`carImageWrap ${Boolean(isLeadTimeEnabled) && 'carImageWrapWithLeadTime imageRef'}`}
                    >
                        <ReactImageFallback
                            src={image}
                            fallbackImage={UNAVAILABLE_CAR_IMAGE}
                            className="carImage"
                            alt={resolveImageAlt(extractViewFromImageUrl(image), trim)}
                        />
                    </div>
                    <div
                        className="equipmentList equipmentListRef"
                        ref={equipmentLisRef}
                        data-testid={`TESTING_EQUIPMENT_${index}`}
                    >
                        {extraFields.seriesEquipment && !!extraFields.seriesEquipment.length && (
                            <>
                                <span className="equipmentList__info">{t('trim.item.strongPoints')}</span>
                                <ul className="equipmentList__list">
                                    {extraFields.seriesEquipment.map((category) => {
                                        if (Array.isArray(category.data)) {
                                            return category.data.map((equipment) => (
                                                <li className="equipmentList__list--item" key={equipment.label}>
                                                    {equipment.label}
                                                </li>
                                            ));
                                        } else {
                                            return Object.keys(category.data).map((key) => (
                                                <li
                                                    className="equipmentList__list--item"
                                                    key={category.data[key].label}
                                                >
                                                    {category.data[key].label}
                                                </li>
                                            ));
                                        }
                                    })}
                                </ul>
                            </>
                        )}
                    </div>
                    <div className="options" data-testid={`TESTING_OPTIONS_${index}`}>
                        <div className="colorOption" id={`TESTING_GO_TO_DETAIL_COLOR_${index}`}>
                            <div className="colorButtonWrapper">
                                <div style={{ backgroundImage: `url('${exteriorColour.images[0]?.url}')` }} />
                            </div>
                            <div className="colorText">
                                <span>
                                    {`${colorsCount} ${t(
                                        `configurator.colors.title${colorsCount > 1 ? '_plural' : ''}`,
                                        {
                                            count: colorsCount,
                                        }
                                    )}`}
                                </span>
                            </div>
                        </div>
                        <div className="engineOption" id={`TESTING_GO_TO_DETAIL_MOTO_${index}`}>
                            <Icon className="engineIcon" name={Icons.EngineSimple} />
                            <div className="engineText">
                                <span>
                                    {`${enginesCount} ${t(
                                        `configurator.motorization.title${enginesCount > 1 ? '_plural' : ''}`,
                                        {
                                            count: enginesCount,
                                        }
                                    )}`}
                                </span>
                            </div>
                        </div>
                    </div>
                    <Benefits benefitsRef={benefitsRef} disableFocus={disabled} offers={offers} />
                    <div className="buttonWrapper">
                        {/*<Button onClick={handleCustomizeClick} primary id={`TESTING_GO_TO_DETAIL_${index}`}>*/}
                        <Link href={modelURL} passHref>
                            <ButtonLink
                                title={title}
                                onClick={handleCustomizeClick}
                                primary
                                id={`TESTING_GO_TO_DETAIL_${index}`}
                                tabIndex={disabled ? -1 : 0}
                            >
                                <span>{t(`trim.configure`)}</span>
                            </ButtonLink>
                        </Link>
                        {/*</Button>*/}
                    </div>
                    {isSOLEnabled && (
                        <div
                            ref={descriptionWrapperRef}
                            className={`descriptionWrapper descriptionWrapperRef ${
                                isMarketGB ? 'descriptionWrapper-gb' : ''
                            }`}
                            data-testid={`TESTING_DESCRIPTION_${index}`}
                        >
                            {hasMonthlyPrices && (
                                <div
                                    className={`descriptionWrapper-payment ${
                                        isMarketGB ? 'descriptionWrapper-payment-gb' : ''
                                    }`}
                                >
                                    {MARKET === MARKETS.GB ? '*' : ''}
                                    {trim.extraFields?.regularPaymentConditions?.[0].text}
                                    <button
                                        className="descriptionWrapper-payment-link"
                                        data-testid={`TESTING_DESCR_${index}`}
                                        onClick={processCalculateSummaryData}
                                    >
                                        {t('label.link.finance')}
                                    </button>
                                </div>
                            )}
                            {modelKeywords}
                        </div>
                    )}
                </div>
            </div>
        </MainTrimContaine>
    );
};

export default TrimItemTemplate;
